import { observedCssMatches } from '../lib/observed-selectors.ts'
import { countOperationKey, measureOperation } from '../../lib/operation-timing.ts'
import { z } from 'zod'
import type { OfflineDom, OfflineEl } from '../layout-shift/rrweb.ts'
import { parseTextQuery, textMatches } from '../../targeting/resolve/text-part.ts'

const TEXT_SELECTOR_VALUE = z.string()

// A text-bearing scope compound with an optional target inside it: `label:has-text("Email") input` is
// the form the catalog generates for a control labeled by a <label>, and the bare `button:has-text("Continue")`
// is the Playwright text pseudo a sightmap review authors from captured DOM evidence.
const HAS_TEXT = /^(.+?):has-text\(("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')\)(?:\s+(.+))?$/

export function querySightmapSelector(dom: OfflineDom, selector: string, parent?: OfflineEl): OfflineEl[] {
  return measureOperation('polish/sightmap/query.querySightmapSelector', () => {
    countOperationKey('sightmap.selector-query', dom, JSON.stringify([parent?.id ?? null, selector]))
    if (selector.startsWith(':scope > ')) {
      if (!parent) return []
      return selector.slice(':scope > '.length).split(' > ').reduce<OfflineEl[]>(
        (nodes, part) => nodes.flatMap(node => node.children.filter(child => child.matches(part))), [parent])
    }
    if (selector.startsWith('internal:text=')) {
      const query = parseTextQuery(selector)
      if (!query) return []
      const matches = (element: OfflineEl) => textMatches(element.text, query)
      return dom.all().filter(element => matches(element) && !element.children.some(matches))
    }
    // The bare has-text engine form: an element containing the text anywhere inside it, whatever the
    // suffix. It grounds an authored selector in captured DOM; it is not the engine's scope filter.
    if (selector.startsWith('internal:has-text=')) {
      const query = parseTextQuery(selector)
      if (!query) return []
      return dom.all().filter(element => textMatches(element.text, { text: query.text, exact: false }))
    }
    const hasText = HAS_TEXT.exec(selector)
    if (hasText) {
      const quoted = hasText[2]!
      let wanted: string
      try {
        wanted = quoted.startsWith("'")
          ? quoted.slice(1, -1).replace(/\\(['\\])/g, '$1')
          : TEXT_SELECTOR_VALUE.parse(JSON.parse(quoted))
      } catch { return [] }
      const scopes = observedCssMatches(dom, hasText[1]!).filter(element => textMatches(element.text, { text: wanted, exact: false }))
      const targets = hasText[3] ? scopes.flatMap(scope => scope.querySelectorAll(hasText[3]!)) : scopes
      return [...new Map(targets.map(target => [target.id, target])).values()]
    }
    return observedCssMatches(dom, selector)
  })
}
